import { useState } from "react";
import { motion } from "framer-motion";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { BRAND } from "../config/brand";
import { track } from "../lib/track";
import { Reveal, SectionLabel } from "./Reveal";
import { ShareDialog } from "./ShareDialog";

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
};

export const ShareSection = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const url = BRAND.shareUrl || window.location.origin;

  const onShare = async () => {
    track("share", lang);
    if (navigator.share) {
      try {
        await navigator.share({ title: "SYNrise", text: t.share.text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    setOpen(true);
  };

  const onCopy = async () => {
    if (await copyText(url)) toast.success(t.share.copied);
  };

  return (
    <section data-testid="share-section" className="relative overflow-hidden rounded-[36px] border border-gold/20 bg-surface/70 px-6 py-12 text-center backdrop-blur-xl sm:px-12 sm:py-16">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-gold/20 blur-[90px]" />
      <Reveal className="relative flex flex-col items-center">
        <SectionLabel index="03">{t.share.kicker}</SectionLabel>
        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-zinc-50 sm:text-5xl" data-testid="share-title">
          {t.share.title}
        </h2>
        <p className="mt-4 max-w-md text-base text-zinc-300">{t.share.sub}</p>
        <motion.button
          onClick={onShare}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          data-testid="share-button"
          className="share-btn group relative mt-10 flex h-16 w-full max-w-sm items-center justify-center gap-3 overflow-hidden rounded-full bg-gold px-8 font-display text-lg font-bold text-ink shadow-[0_20px_60px_-15px_rgba(212,175,55,0.6)]"
        >
          <Share2 className="h-5 w-5 transition-transform duration-500 group-hover:-rotate-12" strokeWidth={2.4} />
          {t.share.button}
        </motion.button>
      </Reveal>
      <ShareDialog open={open} onOpenChange={setOpen} url={url} onCopy={onCopy} />
    </section>
  );
};
